import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HomeIcon, Dumbbell, Target, Users, Menu } from 'lucide-react';

/**
 * Bottom tab bar for phones.
 *
 * On mobile the only way to change page was to open the hamburger drawer, pick
 * from twelve items, and wait for it to slide shut — three interactions for
 * every navigation, with the whole screen covered in between. The four journeys
 * people actually repeat daily now sit one thumb-tap away at the bottom of the
 * screen, and the drawer stays available behind "More" for everything else.
 */
const TABS = [
  { name: 'Home',     href: '/dashboard', icon: HomeIcon },
  { name: 'Workouts', href: '/workouts',  icon: Dumbbell },
  { name: 'Goals',    href: '/goals',     icon: Target },
  { name: 'Social',   href: '/social',    icon: Users },
];

interface MobileNavProps {
  onOpenMenu: () => void;
}

const MobileNav: React.FC<MobileNavProps> = ({ onOpenMenu }) => {
  const location = useLocation();

  return (
    <nav
      aria-label="Main"
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 app-sidebar app-border border-t
                 pb-[env(safe-area-inset-bottom)]"
    >
      <div className="grid grid-cols-5">
        {TABS.map((tab) => {
          // Prefix match so a detail page (/workouts/abc) keeps its parent tab
          // lit rather than leaving every tab dark.
          const isActive =
            location.pathname === tab.href || location.pathname.startsWith(`${tab.href}/`);
          return (
            <Link
              key={tab.href}
              to={tab.href}
              aria-current={isActive ? 'page' : undefined}
              className={`flex flex-col items-center justify-center gap-1 py-2.5 transition-colors
                ${isActive ? 'text-accent' : 'text-ink-dim hover:text-ink-muted'}`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="text-[10px] font-medium">{tab.name}</span>
              {isActive && <div className="h-0.5 w-6 rounded-full bg-accent" />}
            </Link>
          );
        })}
        <button
          onClick={onOpenMenu}
          aria-label="Open navigation menu"
          className="flex flex-col items-center justify-center gap-1 py-2.5 text-ink-dim hover:text-ink-muted transition-colors"
        >
          <Menu className="h-5 w-5" />
          <span className="text-[10px] font-medium">More</span>
        </button>
      </div>
    </nav>
  );
};

export default MobileNav;
